import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import _ from "underscore";
// @mui
import { alpha, styled, useTheme } from "@mui/material/styles";
import { Box, Stack, Drawer, Typography } from "@mui/material";
// hooks
import useResponsive from "../../../hooks/useResponsive";
import useCollapseDrawer from "../../../hooks/useCollapseDrawer";
import { useSelector, useDispatch } from "react-redux";
// utils
import cssStyles from "../../../utils/cssStyles";
// config
import { NAVBAR } from "../../../config";
// components
import Logo from "../../../components/Logo";
import LogoMain from "../../../components/LogoMain";
import Iconify from "../../../components/Iconify";
import Scrollbar from "../../../components/Scrollbar";
import { NavSectionVertical } from "../../../components/nav-section";
//
import { ICONS, getCustomIcon } from "./NavConfig";
import NavbarAccount from './NavbarAccount';
import CollapseButton from "./CollapseButton";
import appPackage from '../../../../package.json';

//reducers

import { getJwt, getInstitutionId, getProfile } from "src/redux/slices/account";
import { showAllGoalStatus, storeAllGoalsStatus } from "src/redux/slices/eventsUI";
import {
  getUserNavigationArray,
} from "../../../redux/slices/navigation";
import { getInstitutionDetails } from "src/redux/slices/institution";

// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const jwt = useSelector(getJwt);
  const institutionId = useSelector(getInstitutionId);
  const profile = useSelector(getProfile);
  const navigationScrollRef = useRef(null);
  const [showScrollCue, setShowScrollCue] = useState(false);

  //navigation
  const userNavigations = useSelector(getUserNavigationArray);
  const institutionDetails = useSelector(getInstitutionDetails);

  let navConfigGrouped = _.groupBy(
    userNavigations?.slice()?.sort((a, b) => a.section_order - b.section_order),
    "section_name"
  );

  let meEventsExists = false
  let EventsExists = false
  let mainIndex = 0
  let subIndexIndex = 0

  let navConfigNew = Object.keys(navConfigGrouped)?.map((key, mainindex) => {
    return {
      subheader: key,
      items: navConfigGrouped[key]
        ?.slice()
        ?.sort((a, b) => a?.order - b?.order)
        ?.map((item, index) => {
          if (item?.link === "events") {
            EventsExists = true

          } else if (item?.link === "me/events") {
            meEventsExists = true
            mainIndex = mainindex
            subIndexIndex = index

          }
          return {
            icon: ICONS?.[item?.icon] ? ICONS?.[item?.icon] : getCustomIcon(item?.icon),
            path: "/" + item?.link,
            title: item?.title,
          };
        }),
    };
  });

  if (meEventsExists && EventsExists) {
    delete navConfigNew?.[mainIndex]?.items?.[subIndexIndex]
  }

  const { pathname } = useLocation();

  const isDesktop = useResponsive("up", "lg");

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  } = useCollapseDrawer();

  const updateScrollCue = useCallback(() => {
    const scrollNode = navigationScrollRef.current;

    if (!scrollNode) {
      return;
    }

    const remainingScroll =
      scrollNode.scrollHeight - scrollNode.scrollTop - scrollNode.clientHeight;
    const hasMoreNavigationBelow = remainingScroll > 4;

    setShowScrollCue((currentValue) =>
      currentValue === hasMoreNavigationBelow
        ? currentValue
        : hasMoreNavigationBelow
    );
  }, []);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    const filteredEvents = userNavigations?.filter(item => item?.link === "events")
    const filteredMeEvents = userNavigations?.filter(item => item?.link === "me/events")
    if (filteredEvents && filteredEvents?.length > 0) {
      if (filteredMeEvents && filteredMeEvents?.length > 0) {
        dispatch(showAllGoalStatus(true))
      }
      else {
        dispatch(showAllGoalStatus(false))
      }
    }
    else {
      dispatch(showAllGoalStatus(false))
    }
  }, [userNavigations]);

  useEffect(() => {
    const scrollNode = navigationScrollRef.current;

    if (!scrollNode) {
      return undefined;
    }

    updateScrollCue();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateScrollCue);

      return () => window.removeEventListener("resize", updateScrollCue);
    }

    const resizeObserver = new ResizeObserver(updateScrollCue);
    resizeObserver.observe(scrollNode);

    if (scrollNode.firstElementChild) {
      resizeObserver.observe(scrollNode.firstElementChild);
    }

    return () => resizeObserver.disconnect();
  }, [isCollapse, updateScrollCue, userNavigations]);

  const renderContent = (
    <Box
      sx={{
        height: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: "center" }),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {!isCollapse ? <LogoMain /> : <Logo />}

          {isDesktop && !isCollapse && (
            <CollapseButton
              onToggleCollapse={onToggleCollapse}
              collapseClick={collapseClick}
            />
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          position: "relative",
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
          "&:hover .sidebar-scroll-cue, &:focus-within .sidebar-scroll-cue": {
            opacity: 1,
            transform: "translateY(0)",
          },
          "@media (hover: none)": {
            "& .sidebar-scroll-cue": {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        <Scrollbar
          scrollableNodeProps={{
            ref: navigationScrollRef,
            onScroll: updateScrollCue,
          }}
          sx={{
            height: 1,
            "& .simplebar-content": {
              minHeight: 1,
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <NavSectionVertical navConfig={navConfigNew} isCollapse={isCollapse} />

          <Box sx={{ flexGrow: 1 }} />
        </Scrollbar>

        {showScrollCue && (
          <Box
            className="sidebar-scroll-cue"
            aria-hidden="true"
            sx={{
              position: "absolute",
              right: 0,
              bottom: 0,
              left: 0,
              height: 48,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              pb: 0.75,
              pointerEvents: "none",
              opacity: 0,
              transform: "translateY(4px)",
              background: (theme) =>
                `linear-gradient(180deg, ${alpha(
                  theme.palette.background.paper,
                  0
                )} 0%, ${alpha(theme.palette.background.paper, 0.92)} 68%)`,
              transition: (theme) =>
                theme.transitions.create(["opacity", "transform"], {
                  duration: theme.transitions.duration.shorter,
                }),
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "50%",
                bgcolor: "background.paper",
                color: "text.secondary",
                boxShadow: (theme) => theme.customShadows.z8,
                animation: "sidebarScrollCue 1.5s ease-in-out infinite",
                "@keyframes sidebarScrollCue": {
                  "0%, 100%": { transform: "translateY(0)" },
                  "50%": { transform: "translateY(3px)" },
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }}
            >
              <Iconify icon="eva:chevron-down-fill" sx={{ width: 18, height: 18 }} />
            </Box>
          </Box>
        )}
      </Box>

      <Box
        component="footer"
        sx={{
          flexShrink: 0,
          px: isCollapse ? 1.5 : 2,
          pt: isCollapse ? 1.5 : 2,
          pb: isCollapse ? 1.5 : 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: "0 -8px 24px rgba(145, 158, 171, 0.08)",
          transition: (theme) =>
            theme.transitions.create(["padding"], {
              duration: theme.transitions.duration.shorter,
            }),
        }}
      >
        {!isCollapse && (
          <Box
            sx={{
              px: 0.5,
              mb: 1.25,
              minWidth: 0,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{ color: "text.disabled", lineHeight: 1.5 }}
              >
                Current workspace
              </Typography>
              <Typography
                variant="subtitle2"
                noWrap
                title={institutionDetails?.name || ""}
                sx={{ mt: 0.25 }}
              >
                {institutionDetails?.name}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              noWrap
              sx={{ color: "text.disabled", pb: 0.125, flexShrink: 0 }}
            >
              v{appPackage?.version}
            </Typography>
          </Box>
        )}

        <NavbarAccount profile={profile} isCollapse={isCollapse} />
      </Box>
    </Box>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: "absolute",
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              overflow: "hidden",
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              overflow: "hidden",
              borderRightStyle: "dashed",
              // bgcolor: "background.default",
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
